import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessagesSquare, LayoutDashboard, Smartphone } from 'lucide-react';

/**
 * SPLIT SCREEN — the demo view: what the buyer is doing, and what the
 * dealership sees, at the same time.
 *
 * Composition ONLY. Both panes are the REAL pages in same-origin iframes — the
 * actual buyer showroom on the left, the actual Sales Desk (or Command Center)
 * on the right. Nothing about either experience is re-implemented here, so
 * there is no second version to drift.
 *
 * SESSION ISOLATION is the whole reason this file can exist. sessionStorage is
 * shared between a tab and its same-origin iframes, so two demo personas alive
 * at once would otherwise overwrite each other's token (and the presenter's own
 * session). Each iframe is therefore NAMED — `yot-slot-left` / `yot-slot-right`
 * — and lib/session.js namespaces every demo key by that slot. `window.name` is
 * per-frame and survives the entry pages' `location.replace`, which a query
 * param would not.
 *
 * Verified absent before relying on it: neither preview nor production sends
 * X-Frame-Options or a CSP frame-ancestors directive, so same-origin framing is
 * permitted.
 */

const RIGHT_PANES = {
    sales: { path: '/demo/automotive/salesperson?demo_slot=right&live=1', icon: MessagesSquare },
    manager: { path: '/demo/automotive/command-center?demo_slot=right&live=1', icon: LayoutDashboard },
};

export default function DemoAutoSplitScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Salesperson by default: it is the seat the dealership actually demos from,
    // and the one the owner asked to stand in front of a prospect with.
    const [right, setRight] = useState('sales');

    useEffect(() => {
        try { localStorage.setItem('yulontap-theme-preference', 'dark'); } catch (_e) { /* ignore */ }
        document.documentElement.classList.add('dark');
        // So the persona switcher knows this is an automotive demo context.
        try { sessionStorage.setItem('yulontap_auto_demo', '1'); } catch (_e) { /* ignore */ }
        // Leaving the split view takes its panes' sessions with it. They are
        // namespaced so they can't be read by anything else, but a stale token
        // left lying around would be silently reused on re-entry.
        return () => {
            try {
                Object.keys(sessionStorage)
                    .filter((key) => /__(left|right)$/.test(key))
                    .forEach((key) => sessionStorage.removeItem(key));
            } catch (_e) { /* ignore */ }
        };
    }, []);

    // Remounting the right pane on toggle is deliberate: the outgoing persona's
    // frame is destroyed, so nothing of it keeps polling behind the new one.
    const rightSrc = useMemo(() => RIGHT_PANES[right].path, [right]);

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col" data-testid="demo-split-screen">
            <header className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-slate-950/95 shrink-0">
                <button onClick={() => navigate('/demo/automotive/buyer')} data-testid="split-exit"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors">
                    <ArrowLeft className="w-4 h-4" />{t('split.exit', 'Exit split screen')}
                </button>
                <span className="ml-auto flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] p-1">
                    {Object.entries(RIGHT_PANES).map(([key, { icon: Icon }]) => (
                        <button key={key} onClick={() => setRight(key)}
                            data-testid={`split-right-${key}`}
                            aria-pressed={right === key}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${
                                right === key ? 'bg-white text-slate-900' : 'text-slate-300 hover:text-white'}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {key === 'sales'
                                ? t('split.rightSales', 'Sales Desk')
                                : t('split.rightManager', 'Command Center')}
                        </button>
                    ))}
                </span>
            </header>

            {/* Side by side on a laptop, stacked on a phone — the same view
                either way, never a different one. */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/10 min-h-0">
                <Pane label={t('split.leftLabel', 'What the buyer sees')} icon={Smartphone}
                    testid="split-pane-buyer" name="yot-slot-left"
                    src="/demo/automotive/buyer?demo_slot=left" />
                <Pane label={right === 'sales'
                        ? t('split.rightLabelSales', 'What the salesperson sees')
                        : t('split.rightLabelManager', 'What the manager sees')}
                    icon={RIGHT_PANES[right].icon}
                    testid="split-pane-staff" name="yot-slot-right"
                    key={right} src={rightSrc} />
            </div>
        </div>
    );
}

const Pane = ({ label, icon: Icon, testid, name, src }) => (
    <section className="flex flex-col min-h-[60vh] lg:min-h-0" data-testid={testid}>
        <p className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em]
                      text-slate-500 border-b border-white/5 shrink-0">
            <Icon className="w-3 h-3" />{label}
        </p>
        <iframe title={label} name={name} src={src}
            data-testid={`${testid}-frame`}
            className="flex-1 w-full border-0 bg-slate-950" />
    </section>
);
